import type {
  CreateMovementPayload,
  CreateCoravinPayload,
  FinishCoravinPayload,
  CoravinBottle,
  CreateWinePayload,
  CancelMovementPayload,
  CellarLocation,
  CellarPlaceRecord,
  LocationFields,
  Movement,
  UpdateMovementPayload,
  TransferWinePayload,
  TransferWineResult,
  Wine,
  WineFields,
} from "../types";
import {
  categoryApiValue,
  normalizeCoravin,
  normalizeLocation,
  normalizeMovement,
  normalizeWine,
} from "./wine";
import {
  offlinePersistence,
  type OfflineReadProjection,
  type OfflineMovementOperation,
  type OfflineMovementSyncResult,
} from "./offline";
import {
  type WineAiAnalysisResult,
} from "./wine-photo-ai";
import { fetchWithSessionRetry } from "./session-auth";

const API_ROOT = "/api";
export const OFFLINE_DATA_CHANGED_EVENT = "ma-cave:offline-data-changed";
export const CONNECTIVITY_CHANGED_EVENT = "ma-cave:connectivity-changed";
export const SECURE_SYNC_REQUESTED_EVENT = "ma-cave:secure-sync-requested";

export interface SynchronizationVersion {
  revision: number;
  updated_at: string;
}

let reportedOnline: boolean | null = null;

class NetworkFailure extends Error {
  readonly cause: unknown;

  constructor(cause?: unknown) {
    super("Connexion au serveur impossible.");
    this.name = "NetworkFailure";
    this.cause = cause;
  }
}

function reportConnectivity(online: boolean): void {
  if (reportedOnline === online) return;
  reportedOnline = online;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(CONNECTIVITY_CHANGED_EVENT, { detail: { online } }),
    );
  }
}

function browserIsOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function isNetworkFailure(error: unknown): boolean {
  return error instanceof NetworkFailure;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  const bodyIsFormData =
    typeof FormData !== "undefined" && init?.body instanceof FormData;
  try {
    response = await fetchWithSessionRetry(`${API_ROOT}${path}`, {
      ...init,
      cache: "no-store",
      headers: {
        ...(init?.body && !bodyIsFormData
          ? { "Content-Type": "application/json" }
          : {}),
        Accept: "application/json",
        ...init?.headers,
      },
    });
    reportConnectivity(true);
  } catch (error) {
    reportConnectivity(false);
    throw new NetworkFailure(error);
  }

  if (!response.ok) {
    let message = "Une erreur est survenue. Réessayez dans un instant.";
    let code: string | undefined;
    let details: unknown;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        erreur?: { message?: string; code?: string; details?: unknown };
      };
      message = body.erreur?.message ?? body.error ?? body.message ?? message;
      code = body.erreur?.code;
      details = body.erreur?.details;
    } catch {
      // La réponse peut être vide ou non JSON.
    }
    throw new ApiError(message, response.status, code, details);
  }

  if (response.status === 204) return undefined as T;
  const value = (await response.json()) as T;
  return value;
}

function scheduleSecureSynchronization(projectionRefreshPending: boolean): void {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent(SECURE_SYNC_REQUESTED_EVENT, {
        detail: { projection_refresh_pending: projectionRefreshPending },
      }),
    );
  }, 500);
}

async function mutationRequest<T>(
  path: string,
  init: RequestInit,
  options: { refreshProjection?: boolean } = {},
): Promise<T> {
  const value = await request<T>(path, init);
  let projectionRefreshPending = false;
  if (options.refreshProjection !== false) {
    try {
      await refreshLocalProjectionFromServer();
    } catch {
      // L’écriture distante est déjà confirmée : une panne de reprojection ne
      // doit jamais devenir un faux échec d’enregistrement. Le signal ci-dessous
      // planifie une nouvelle passe et rend l’attente visible via la synchro.
      projectionRefreshPending = true;
    }
  }
  scheduleSecureSynchronization(projectionRefreshPending);
  return value;
}

const GEMINI_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/heic",
  "image/heif",
]);
const MAX_GEMINI_PHOTO_BYTES = 12 * 1024 * 1024;
const MAX_GEMINI_PHOTO_FILE_BYTES = 8 * 1024 * 1024;

function assertGeminiPhotos(files: readonly File[]): void {
  if (files.length < 1 || files.length > 2) {
    throw new ApiError(
      "Ajoutez la photo avant et, si vous le souhaitez, celle du dos.",
      400,
      "PHOTOS_INVALIDES",
    );
  }
  if (files.some((file) => !GEMINI_IMAGE_MIME_TYPES.has(file.type))) {
    throw new ApiError(
      "Utilisez une photo JPEG, PNG, WebP, HEIC ou HEIF.",
      400,
      "FORMAT_PHOTO_INVALIDE",
    );
  }
  if (files.some((file) => file.size > MAX_GEMINI_PHOTO_FILE_BYTES)) {
    throw new ApiError(
      "Chaque photo doit peser au maximum 8 Mo.",
      413,
      "PHOTO_TROP_LOURDE",
    );
  }
  const totalBytes = files.reduce((total, file) => total + file.size, 0);
  if (totalBytes > MAX_GEMINI_PHOTO_BYTES) {
    throw new ApiError(
      "Les deux photos doivent peser moins de 12 Mo au total.",
      413,
      "PHOTOS_TROP_LOURDES",
    );
  }
}

async function requiredLocalValue<T>(
  read: () => Promise<T | null>,
  emptyMessage: string,
): Promise<T> {
  try {
    const cached = await read();
    if (cached !== null) return cached;
  } catch {
    // Le message utile ci-dessous couvre aussi un coffre local indisponible.
  }
  throw new ApiError(emptyMessage, 0, "COPIE_LOCALE_ABSENTE");
}

function notifyOfflineDataChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(OFFLINE_DATA_CHANGED_EVENT));
  }
}

function unwrapWine(raw: unknown): Wine {
  const record = (raw ?? {}) as Record<string, unknown>;
  const value = (record.vin ?? record.wine ?? record.data ?? record) as Record<
    string,
    unknown
  >;
  const movements = record.mouvements ?? record.movements;
  return normalizeWine(
    movements && !value.mouvements && !value.movements
      ? { ...value, mouvements: movements }
      : value,
  );
}

function unwrapMovement(raw: unknown): Movement {
  const record = (raw ?? {}) as Record<string, unknown>;
  const value = (record.mouvement ?? record.movement ?? record.data ?? record) as Record<
    string,
    unknown
  >;
  return normalizeMovement(value);
}

function unwrapOptionalMovement(raw: unknown): Movement | null {
  const record = (raw ?? {}) as Record<string, unknown>;
  if (
    (Object.prototype.hasOwnProperty.call(record, "mouvement") &&
      record.mouvement === null) ||
    (Object.prototype.hasOwnProperty.call(record, "movement") &&
      record.movement === null)
  ) {
    return null;
  }
  return unwrapMovement(raw);
}

function unwrapLocation(raw: unknown): CellarLocation {
  const record = (raw ?? {}) as Record<string, unknown>;
  const value = (record.emplacement ?? record.location ?? record.data ?? record) as Record<
    string,
    unknown
  >;
  return normalizeLocation(value);
}

function normalizePlace(raw: Record<string, unknown>): CellarPlaceRecord {
  return {
    id: String(raw.id ?? raw.lieu_id ?? ""),
    nom: String(raw.nom ?? raw.name ?? "Lieu sans nom").trim(),
    ...(typeof (raw.date_modification ?? raw.updated_at) === "string"
      ? { date_modification: String(raw.date_modification ?? raw.updated_at) }
      : {}),
    nombre_emplacements: Number(
      raw.nombre_emplacements ?? raw.location_count ?? 0,
    ),
    nombre_fiches: Number(raw.nombre_fiches ?? raw.reference_count ?? 0),
    stock: Number(raw.stock ?? raw.nombre_bouteilles ?? raw.bottle_count ?? 0),
  };
}

function ifMatchHeaders(expectedModification?: string | null): HeadersInit | undefined {
  return expectedModification
    ? { "If-Match": `"${expectedModification}"` }
    : undefined;
}

function unwrapPlace(raw: unknown): CellarPlaceRecord {
  const record = (raw ?? {}) as Record<string, unknown>;
  const value = (record.lieu ?? record.place ?? record.data ?? record) as Record<
    string,
    unknown
  >;
  return normalizePlace(value);
}

function unwrapSynchronizationVersion(raw: unknown): SynchronizationVersion {
  const record = (raw ?? {}) as Record<string, unknown>;
  if (
    !Number.isSafeInteger(record.revision) ||
    Number(record.revision) < 0 ||
    typeof record.updated_at !== "string" ||
    record.updated_at.trim() === ""
  ) {
    throw new ApiError(
      "Le serveur n’a pas renvoyé une version de synchronisation valide.",
      502,
      "REPONSE_SYNCHRONISATION_INVALIDE",
    );
  }
  return {
    revision: Number(record.revision),
    updated_at: record.updated_at,
  };
}

const MAX_PROJECTION_WINES = 20_000;
const MAX_PROJECTION_PLACES = 5_000;
const MAX_PROJECTION_LOCATIONS = 20_000;
const MAX_PROJECTION_MOVEMENTS_PER_WINE = 50_000;
const MAX_PROJECTION_MOVEMENTS = 200_000;
const MAX_PROJECTION_ID_LENGTH = 512;
const PROJECTION_CATEGORIES = new Set([
  "Rouge",
  "Blanc",
  "Rosé",
  "Orange",
  "Effervescent",
  "Liquoreux",
  "Vin fortifié / vin doux naturel",
  "Autre",
]);
const PROJECTION_MOVEMENT_REASONS = new Set([
  "Stock initial",
  "Achat",
  "Bouteille bue",
  "Correction",
  "Transfert",
]);

interface RawSynchronizationProjection extends Record<string, unknown> {
  vins: unknown[];
  lieux: unknown[];
  emplacements: unknown[];
}

function projectionRecord(value: unknown, label: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return value as Record<string, unknown>;
}

function projectionId(value: unknown, label: string): string {
  if (
    typeof value !== "string" ||
    value.trim() === "" ||
    value.length > MAX_PROJECTION_ID_LENGTH
  ) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return value;
}

function optionalProjectionId(value: unknown, label: string): string | null {
  return value === null ? null : projectionId(value, label);
}

function projectionInteger(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return value;
}

function projectionText(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim() === "" || value.length > 10_000) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return value;
}

function optionalProjectionText(value: unknown, label: string): string | null {
  if (value === undefined || value === null) return null;
  return projectionText(value, label);
}

function optionalProjectionInteger(value: unknown, label: string): number | null {
  if (value === undefined || value === null) return null;
  return projectionInteger(value, label);
}

function optionalProjectionNumber(value: unknown, label: string): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return value;
}

function projectionDate(value: unknown, label: string): string {
  const date = projectionText(value, label);
  const parsed = Date.parse(`${date}T00:00:00.000Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(parsed) ||
      new Date(parsed).toISOString().slice(0, 10) !== date) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
  return date;
}

function projectionTimestamp(value: unknown, label: string): void {
  if (!Number.isFinite(Date.parse(projectionText(value, label)))) {
    throw new ApiError(`${label} est invalide.`, 502, "REPONSE_PROJECTION_INVALIDE");
  }
}

function assertProjectionContract(
  record: Record<string, unknown>,
): asserts record is RawSynchronizationProjection {
  if (
    !Array.isArray(record.vins) ||
    record.vins.length > MAX_PROJECTION_WINES ||
    !Array.isArray(record.lieux) ||
    record.lieux.length > MAX_PROJECTION_PLACES ||
    !Array.isArray(record.emplacements) ||
    record.emplacements.length > MAX_PROJECTION_LOCATIONS
  ) {
    throw new ApiError(
      "Le serveur n’a pas renvoyé une projection de cave valide.",
      502,
      "REPONSE_PROJECTION_INVALIDE",
    );
  }

  const placesById = new Map<string, string>();
  for (const candidate of record.lieux) {
    const place = projectionRecord(candidate, "Un lieu");
    const id = projectionId(place.id, "L’identifiant d’un lieu");
    if (placesById.has(id)) {
      throw new ApiError("Un lieu est dupliqué.", 502, "REPONSE_PROJECTION_INVALIDE");
    }
    const name = projectionText(place.nom, "Le nom d’un lieu");
    placesById.set(id, name);
    optionalProjectionText(place.date_modification, "La date d’un lieu");
    projectionInteger(place.nombre_emplacements, "Le nombre d’emplacements");
    projectionInteger(place.nombre_fiches, "Le nombre de fiches d’un lieu");
    projectionInteger(place.stock, "Le stock d’un lieu");
  }

  const locationsById = new Map<string, string | null>();
  for (const candidate of record.emplacements) {
    const location = projectionRecord(candidate, "Un emplacement");
    const id = projectionId(location.id, "L’identifiant d’un emplacement");
    if (locationsById.has(id)) {
      throw new ApiError(
        "Un emplacement est dupliqué.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    const placeId = optionalProjectionId(
      location.lieu_id,
      "Le lieu d’un emplacement",
    );
    if (placeId !== null && !placesById.has(placeId)) {
      throw new ApiError(
        "Un emplacement référence un lieu absent.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    locationsById.set(id, placeId);
    projectionText(location.nom, "Le nom d’un emplacement");
    projectionText(location.libelle, "Le libellé d’un emplacement");
    optionalProjectionText(
      location.date_modification,
      "La date d’un emplacement",
    );
    if (
      placeId === null
        ? location.lieu !== null
        : location.lieu !== placesById.get(placeId)
    ) {
      throw new ApiError(
        "Un emplacement ne correspond pas à son lieu.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    if (location.capacite_totale !== null) {
      projectionInteger(location.capacite_totale, "La capacité d’un emplacement");
    }
    projectionInteger(location.nombre_fiches, "Le nombre de fiches d’un emplacement");
    projectionInteger(location.stock, "Le stock d’un emplacement");
  }

  const wineIds = new Set<string>();
  const movementIds = new Set<string>();
  const coravinIds = new Set<string>();
  let movementCount = 0;
  let coravinCount = 0;
  for (const candidate of record.vins) {
    const wine = projectionRecord(candidate, "Une fiche vin");
    const id = projectionId(wine.id, "L’identifiant d’une fiche vin");
    if (wineIds.has(id)) {
      throw new ApiError("Une fiche vin est dupliquée.", 502, "REPONSE_PROJECTION_INVALIDE");
    }
    wineIds.add(id);
    projectionText(wine.producteur, "Le producteur d’une fiche vin");
    projectionText(wine.nom, "Le nom d’une fiche vin");
    const category = projectionText(
      wine.categorie,
      "La catégorie d’une fiche vin",
    );
    if (!PROJECTION_CATEGORIES.has(category)) {
      throw new ApiError(
        "La catégorie d’une fiche vin est inconnue.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    projectionText(wine.format, "Le format d’une fiche vin");
    optionalProjectionText(wine.appellation, "L’appellation d’une fiche vin");
    optionalProjectionText(wine.region, "La région d’une fiche vin");
    optionalProjectionText(wine.cepages, "Les cépages d’une fiche vin");
    optionalProjectionText(
      wine.vinification_elevage,
      "La vinification d’une fiche vin",
    );
    optionalProjectionInteger(wine.millesime, "Le millésime d’une fiche vin");
    optionalProjectionInteger(wine.debut_deg, "Le début de garde d’une fiche vin");
    optionalProjectionInteger(wine.fin_deg, "La fin de garde d’une fiche vin");
    optionalProjectionText(wine.emplacement, "L’emplacement d’une fiche vin");
    optionalProjectionText(wine.lieu, "Le lieu d’une fiche vin");
    optionalProjectionText(wine.note, "La note d’une fiche vin");
    optionalProjectionText(wine.date_creation, "La date de création d’une fiche vin");
    optionalProjectionText(
      wine.date_modification,
      "La date de modification d’une fiche vin",
    );
    optionalProjectionNumber(
      wine.dernier_prix_achat,
      "Le dernier prix d’achat d’une fiche vin",
    );
    if (
      wine.a_une_note_degustation !== undefined &&
      typeof wine.a_une_note_degustation !== "boolean"
    ) {
      throw new ApiError(
        "L’indicateur de dégustation d’une fiche vin est invalide.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    const stock = projectionInteger(wine.stock, "Le stock d’une fiche vin");
    const primaryLocationId = optionalProjectionId(
      wine.emplacement_id,
      "L’emplacement principal d’une fiche vin",
    );
    if (primaryLocationId !== null && !locationsById.has(primaryLocationId)) {
      throw new ApiError(
        "Une fiche vin référence un emplacement absent.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    const primaryPlaceId = optionalProjectionId(
      wine.lieu_id,
      "Le lieu principal d’une fiche vin",
    );
    if (primaryPlaceId !== null && !placesById.has(primaryPlaceId)) {
      throw new ApiError(
        "Une fiche vin référence un lieu absent.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    if (
      primaryLocationId !== null &&
      primaryPlaceId !== locationsById.get(primaryLocationId)
    ) {
      throw new ApiError(
        "Une fiche vin ne correspond pas au lieu de son emplacement.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    if (!Array.isArray(wine.stocks_par_emplacement)) {
      throw new ApiError(
        "La ventilation d’une fiche vin est absente.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    const stockLocationIds = new Set<string>();
    const stocksByLocationId = new Map<string | null, number>();
    let distributedStock = 0;
    for (const candidateStock of wine.stocks_par_emplacement) {
      const locationStock = projectionRecord(candidateStock, "Une ventilation de stock");
      const locationId = optionalProjectionId(
        locationStock.emplacement_id,
        "L’emplacement d’une ventilation",
      );
      const locationKey = locationId ?? "__sans_emplacement__";
      if (stockLocationIds.has(locationKey)) {
        throw new ApiError(
          "Une ventilation de stock est dupliquée.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      stockLocationIds.add(locationKey);
      if (locationId !== null && !locationsById.has(locationId)) {
        throw new ApiError(
          "Une ventilation référence un emplacement absent.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      const placeId = optionalProjectionId(
        locationStock.lieu_id,
        "Le lieu d’une ventilation",
      );
      optionalProjectionText(
        locationStock.lieu,
        "Le libellé du lieu d’une ventilation",
      );
      optionalProjectionText(
        locationStock.emplacement,
        "Le libellé d’une ventilation",
      );
      optionalProjectionText(
        locationStock.emplacement_nom,
        "Le nom d’emplacement d’une ventilation",
      );
      if (placeId !== null && !placesById.has(placeId)) {
        throw new ApiError(
          "Une ventilation référence un lieu absent.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      if (
        locationId !== null &&
        placeId !== locationsById.get(locationId)
      ) {
        throw new ApiError(
          "Une ventilation ne correspond pas à son lieu.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      const locationQuantity = projectionInteger(
        locationStock.stock,
        "Le stock d’une ventilation",
      );
      stocksByLocationId.set(locationId, locationQuantity);
      distributedStock += locationQuantity;
      if (!Number.isSafeInteger(distributedStock)) {
        throw new ApiError(
          "Le stock ventilé d’une fiche vin est invalide.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
    }
    if (distributedStock !== stock) {
      throw new ApiError(
        "La ventilation d’une fiche vin ne correspond pas à son stock.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }

    if (
      !Array.isArray(wine.mouvements) ||
      wine.mouvements.length > MAX_PROJECTION_MOVEMENTS_PER_WINE ||
      movementCount + wine.mouvements.length > MAX_PROJECTION_MOVEMENTS
    ) {
      throw new ApiError(
        "L’historique d’une fiche vin est invalide.",
        502,
        "REPONSE_PROJECTION_INVALIDE",
      );
    }
    movementCount += wine.mouvements.length;
    const movementsById = new Map<string, Record<string, unknown>>();
    for (const candidateMovement of wine.mouvements) {
      const movement = projectionRecord(candidateMovement, "Un mouvement");
      const movementId = projectionId(movement.id, "L’identifiant d’un mouvement");
      if (movementIds.has(movementId)) {
        throw new ApiError(
          "Un mouvement est dupliqué.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      movementIds.add(movementId);
      movementsById.set(movementId, movement);
      if (projectionId(movement.vin_id, "La fiche d’un mouvement") !== id) {
        throw new ApiError(
          "Un mouvement référence une autre fiche vin.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      if (
        typeof movement.quantite !== "number" ||
        !Number.isSafeInteger(movement.quantite)
      ) {
        throw new ApiError(
          "La quantité d’un mouvement est invalide.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      const reason = projectionText(movement.motif, "Le motif d’un mouvement");
      if (!PROJECTION_MOVEMENT_REASONS.has(reason)) {
        throw new ApiError(
          "Le motif d’un mouvement est inconnu.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      projectionText(movement.date_creation, "La date de création d’un mouvement");
      optionalProjectionText(
        movement.date_modification,
        "La date de modification d’un mouvement",
      );
      if (movement.mouvement_annule_id !== undefined) {
        optionalProjectionId(
          movement.mouvement_annule_id,
          "Le mouvement annulé d’un mouvement",
        );
      }
      optionalProjectionNumber(
        movement.prix_unitaire,
        "Le prix d’un mouvement",
      );
      optionalProjectionText(movement.vendeur, "Le vendeur d’un mouvement");
      optionalProjectionText(movement.note, "La note d’un mouvement");
      if (movement.transfert_id !== undefined) {
        optionalProjectionId(
          movement.transfert_id,
          "Le transfert d’un mouvement",
        );
      }
      if (movement.coravin_id !== undefined) {
        optionalProjectionId(movement.coravin_id, "Le suivi Coravin d’un mouvement");
      }
      if (
        movement.date_effective !== null &&
        (typeof movement.date_effective !== "string" ||
          movement.date_effective.trim() === "")
      ) {
        throw new ApiError(
          "La date effective d’un mouvement est invalide.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      const movementLocationId = optionalProjectionId(
        movement.emplacement_id,
        "L’emplacement d’un mouvement",
      );
      if (
        movementLocationId !== null &&
        !locationsById.has(movementLocationId)
      ) {
        throw new ApiError(
          "Un mouvement référence un emplacement absent.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      const movementPlaceId = optionalProjectionId(
        movement.lieu_id,
        "Le lieu d’un mouvement",
      );
      if (movementPlaceId !== null && !placesById.has(movementPlaceId)) {
        throw new ApiError(
          "Un mouvement référence un lieu absent.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
      if (
        movementLocationId !== null &&
        movementPlaceId !== locationsById.get(movementLocationId)
      ) {
        throw new ApiError(
          "Un mouvement ne correspond pas au lieu de son emplacement.",
          502,
          "REPONSE_PROJECTION_INVALIDE",
        );
      }
    }

    // Les coffres antérieurs au suivi Coravin peuvent omettre cette collection.
    if (wine.coravins !== undefined && !Array.isArray(wine.coravins)) {
      throw new ApiError("Le suivi Coravin d’une fiche vin est invalide.", 502, "REPONSE_PROJECTION_INVALIDE");
    }
    const coravins = (wine.coravins ?? []) as unknown[];
    coravinCount += coravins.length;
    if (coravins.length > MAX_PROJECTION_MOVEMENTS_PER_WINE || coravinCount > MAX_PROJECTION_MOVEMENTS) {
      throw new ApiError("Le suivi Coravin d’une fiche vin est trop volumineux.", 502, "REPONSE_PROJECTION_INVALIDE");
    }
    const wineCoravinIds = new Set<string>();
    const activeByLocation = new Map<string | null, number>();
    const cancelledMovementIds = new Set(
      [...movementsById.values()].map((movement) => movement.mouvement_annule_id),
    );
    for (const candidateCoravin of coravins) {
      const bottle = projectionRecord(candidateCoravin, "Une bouteille coravinée");
      const bottleId = projectionId(bottle.id, "L’identifiant d’une bouteille coravinée");
      if (coravinIds.has(bottleId)) {
        throw new ApiError("Une bouteille coravinée est dupliquée.", 502, "REPONSE_PROJECTION_INVALIDE");
      }
      coravinIds.add(bottleId);
      wineCoravinIds.add(bottleId);
      if (projectionId(bottle.vin_id, "La fiche d’une bouteille coravinée") !== id) {
        throw new ApiError("Une bouteille coravinée référence une autre fiche vin.", 502, "REPONSE_PROJECTION_INVALIDE");
      }
      const locationId = optionalProjectionId(bottle.emplacement_id, "L’emplacement d’une bouteille coravinée");
      const placeId = optionalProjectionId(bottle.lieu_id, "Le lieu d’une bouteille coravinée");
      if ((locationId !== null && !locationsById.has(locationId)) ||
          (placeId !== null && !placesById.has(placeId)) ||
          (locationId !== null && placeId !== locationsById.get(locationId)) ||
          (locationId === null && placeId !== null)) {
        throw new ApiError("La localisation d’une bouteille coravinée est incohérente.", 502, "REPONSE_PROJECTION_INVALIDE");
      }
      optionalProjectionText(bottle.emplacement, "L’emplacement d’une bouteille coravinée");
      optionalProjectionText(bottle.lieu, "Le lieu d’une bouteille coravinée");
      optionalProjectionText(bottle.note, "La note d’une bouteille coravinée");
      projectionTimestamp(bottle.date_creation, "La date de création d’une bouteille coravinée");
      projectionTimestamp(bottle.date_modification, "La date de modification d’une bouteille coravinée");
      const startedOn = projectionDate(bottle.date_coravin, "La date du prélèvement Coravin");
      const finishId = optionalProjectionId(bottle.mouvement_fin_id, "Le mouvement de fin d’une bouteille coravinée");
      if (bottle.date_fin === null) {
        if (finishId !== null) {
          throw new ApiError("Une bouteille coravinée active référence une fin de bouteille.", 502, "REPONSE_PROJECTION_INVALIDE");
        }
        activeByLocation.set(locationId, (activeByLocation.get(locationId) ?? 0) + 1);
      } else {
        const finishedOn = projectionDate(bottle.date_fin, "La date de fin d’une bouteille coravinée");
        const finish = finishId === null ? undefined : movementsById.get(finishId);
        if (finishedOn < startedOn || !finish || finish.quantite !== -1 ||
            finish.motif !== "Bouteille bue" || finish.emplacement_id !== locationId ||
            finish.date_effective !== finishedOn ||
            (finish.coravin_id !== undefined && finish.coravin_id !== bottleId) ||
            cancelledMovementIds.has(finishId)) {
          throw new ApiError("La fin d’une bouteille coravinée ne correspond pas à son historique.", 502, "REPONSE_PROJECTION_INVALIDE");
        }
      }
    }
    for (const [locationId, count] of activeByLocation) {
      if (count > (stocksByLocationId.get(locationId) ?? 0)) {
        throw new ApiError("Le nombre de bouteilles coravinées dépasse le stock de leur emplacement.", 502, "REPONSE_PROJECTION_INVALIDE");
      }
    }
    for (const movement of movementsById.values()) {
      if (typeof movement.coravin_id === "string" && !wineCoravinIds.has(movement.coravin_id)) {
        throw new ApiError("Un mouvement référence une bouteille coravinée absente.", 502, "REPONSE_PROJECTION_INVALIDE");
      }
    }
  }
}

function unwrapSynchronizationProjection(raw: unknown): OfflineReadProjection {
  const record = (raw ?? {}) as Record<string, unknown>;
  const version = unwrapSynchronizationVersion(record);
  assertProjectionContract(record);
  return {
    ...version,
    wines: record.vins.map((wine) =>
      normalizeWine(wine as Record<string, unknown>),
    ),
    places: record.lieux.map((place) =>
      normalizePlace(place as Record<string, unknown>),
    ),
    locations: record.emplacements.map((location) =>
      normalizeLocation(location as Record<string, unknown>),
    ),
  };
}

function wineBody(
  payload: WineFields,
  options: { includeLocation?: boolean } = {},
): Record<string, unknown> {
  return {
    producteur: payload.producteur,
    nom: payload.nom,
    appellation: payload.appellation || null,
    region: payload.region || null,
    cepages: payload.cepages.trim() || null,
    vinification_elevage: payload.vinification_elevage.trim() || null,
    millesime: payload.millesime,
    categorie: categoryApiValue(payload.categorie),
    format: payload.format || "75 cl",
    ...(options.includeLocation === false
      ? {}
      : { emplacement_id: payload.emplacement_id }),
    debut_degustation: payload.debut_deg,
    fin_degustation: payload.fin_deg,
    note: payload.note || null,
  };
}

async function cachedWine(id: string): Promise<Wine | null> {
  return offlinePersistence.getCachedWine(id);
}

export async function refreshLocalProjectionFromServer(): Promise<SynchronizationVersion> {
  const projection = unwrapSynchronizationProjection(
    await request<unknown>("/synchronisation/projection"),
  );
  await offlinePersistence.replaceProjection(projection);
  notifyOfflineDataChanged();
  return {
    revision: projection.revision,
    updated_at: projection.updated_at,
  };
}

function createClientOperationId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
}

async function sendOfflineMovement(
  operation: OfflineMovementOperation,
): Promise<OfflineMovementSyncResult> {
  const body = {
    ...operation.payload,
    client_operation_id: operation.client_operation_id,
  };
  const raw = await mutationRequest<unknown>(
    `/vins/${encodeURIComponent(operation.wine_id)}/mouvements/${operation.action}`,
    { method: "POST", body: JSON.stringify(body) },
    { refreshProjection: false },
  );
  const record = (raw ?? {}) as Record<string, unknown>;
  const stock = Number(record.stock);
  if (!Number.isInteger(stock) || stock < 0) {
    throw new ApiError(
      "Le serveur n’a pas renvoyé un stock valide.",
      502,
      "REPONSE_STOCK_INVALIDE",
    );
  }
  const result = {
    movement: unwrapOptionalMovement(raw),
    stock,
    ...(record.stock_emplacement == null
      ? {}
      : { stock_emplacement: Number(record.stock_emplacement) }),
    ...(Array.isArray(record.stocks_par_emplacement)
      ? {
          stocks_par_emplacement:
            normalizeWine({
              id: operation.wine_id,
              stock,
              stocks_par_emplacement: record.stocks_par_emplacement,
            }).stocks_par_emplacement ?? [],
        }
      : {}),
  } satisfies OfflineMovementSyncResult;
  return result;
}

export async function syncOfflineChanges(
  confirmedOperations?: readonly OfflineMovementOperation[],
) {
  const result = await offlinePersistence.syncQueuedMovements(
    sendOfflineMovement,
    { confirmedOperations },
  );
  if (result.synced_count > 0) notifyOfflineDataChanged();
  return result;
}

async function queueableMovement(
  wineId: string,
  payload: CreateMovementPayload,
): Promise<Movement | null> {
  const clientOperationId = createClientOperationId();
  const queued = await offlinePersistence.queueMovement(wineId, payload, {
    clientOperationId,
  });
  notifyOfflineDataChanged();
  // Le clic ne touche jamais D1 : vault-sync compare/pousse d’abord le coffre,
  // puis lui seul vide l’outbox quand l’ordre causal est établi.
  scheduleSecureSynchronization(false);
  return queued.operation.temporary_movement;
}

async function assertWineHasNoPendingMovements(wineId: string): Promise<void> {
  const pending = await offlinePersistence.listQueuedMovements();
  if (pending.some((operation) => operation.wine_id === wineId)) {
    throw new ApiError(
      "Synchronisez d’abord les mouvements en attente de cette fiche avant de continuer.",
      409,
      "MOUVEMENTS_EN_ATTENTE",
    );
  }
}

// Une réponse perdue ne transforme pas un nouvel essai du même formulaire en
// seconde bouteille. La clé est libérée seulement après une réponse certaine.
const unresolvedCoravinOperations = new Map<string, string>();

async function coravinMutation(
  wineId: string,
  path: string,
  init: RequestInit,
  operationPayload?: Record<string, unknown>,
): Promise<unknown> {
  if (browserIsOffline()) {
    throw new ApiError(
      "Le suivi Coravin nécessite une connexion. Les bouteilles déjà coravinées restent consultables hors ligne.",
      0,
      "ACTION_INDISPONIBLE_HORS_LIGNE",
    );
  }
  await assertWineHasNoPendingMovements(wineId);
  const operationKey = operationPayload ? `${path}:${JSON.stringify(operationPayload)}` : null;
  if (operationKey && operationPayload) {
    const operationId = unresolvedCoravinOperations.get(operationKey) ?? createClientOperationId();
    unresolvedCoravinOperations.set(operationKey, operationId);
    init = { ...init, body: JSON.stringify({ ...operationPayload, client_operation_id: operationId }) };
  }
  try {
    const result = await mutationRequest<unknown>(path, init);
    if (operationKey) unresolvedCoravinOperations.delete(operationKey);
    return result;
  } catch (error) {
    if (isNetworkFailure(error) || !(error instanceof ApiError)) {
      throw new ApiError(
        "La réponse du serveur n’a pas pu être confirmée. Synchronisez la fiche pour vérifier le suivi Coravin avant de réessayer.",
        0,
        "ACTION_INDISPONIBLE_HORS_LIGNE",
      );
    }
    if (operationKey) unresolvedCoravinOperations.delete(operationKey);
    throw error;
  }
}

export const api = {
  async getSynchronizationVersion(): Promise<SynchronizationVersion> {
    return unwrapSynchronizationVersion(
      await request<unknown>("/synchronisation/version"),
    );
  },

  async analyzeWinePhotos(files: File[]): Promise<WineAiAnalysisResult> {
    assertGeminiPhotos(files);
    const form = new FormData();
    form.append("photo_avant", files[0], files[0].name);
    if (files[1]) form.append("photo_arriere", files[1], files[1].name);
    return request<WineAiAnalysisResult>("/ia/analyser-etiquettes", {
      method: "POST",
      body: form,
    });
  },

  async listWines(view: "en_stock" | "epuises" = "en_stock"): Promise<Wine[]> {
    return requiredLocalValue(
      () => offlinePersistence.getCachedInventory(view),
      "Cet inventaire n’a pas encore été enregistré sur cet appareil.",
    );
  },

  async getWine(id: string): Promise<Wine> {
    return requiredLocalValue(
      () => cachedWine(id),
      "Cette fiche n’a pas encore été enregistrée sur cet appareil.",
    );
  },

  async createWine(payload: CreateWinePayload): Promise<Wine> {
    const { entree, repartition_initiale, ...vin } = payload;
    return unwrapWine(
      await mutationRequest<unknown>("/vins", {
        method: "POST",
        body: JSON.stringify({
          ...wineBody(vin),
          quantite_initiale: entree.quantite,
          date_entree: entree.date_effective || null,
          prix_unitaire: entree.prix_unitaire,
          vendeur: entree.vendeur || null,
          note_entree: entree.note || null,
          ...(repartition_initiale
            ? {
                repartition_initiale: repartition_initiale.map((item) => ({
                  emplacement_id: item.emplacement_id,
                  quantite: item.quantite,
                })),
              }
            : {}),
        }),
      }),
    );
  },

  async updateWine(
    id: string,
    payload: WineFields,
    expectedModification?: string | null,
  ): Promise<Wine> {
    return unwrapWine(
      await mutationRequest<unknown>(`/vins/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: ifMatchHeaders(expectedModification),
        body: JSON.stringify({
          ...wineBody(payload, { includeLocation: false }),
        }),
      }),
    );
  },

  async deleteWine(id: string, expectedModification?: string | null): Promise<void> {
    await mutationRequest<void>(`/vins/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: ifMatchHeaders(expectedModification),
    });
  },

  async createMovement(
    wineId: string,
    payload: CreateMovementPayload,
  ): Promise<Movement | null> {
    const normalizedPayload = {
      ...payload,
      emplacement_id: payload.emplacement_id ?? null,
    };
    const action =
      payload.motif === "Achat"
        ? "achat"
        : payload.motif === "Bouteille bue"
          ? "boire"
          : "correction";
    if (action === "boire" || action === "correction") {
      return queueableMovement(wineId, normalizedPayload);
    }
    if (browserIsOffline()) {
      throw new ApiError(
        "L’ajout de bouteilles nécessite une connexion.",
        0,
        "ACTION_INDISPONIBLE_HORS_LIGNE",
      );
    }
    const body =
      action === "achat"
        ? {
            quantite: Math.abs(payload.quantite),
            date_effective: payload.date_effective || null,
            prix_unitaire: payload.prix_unitaire,
            vendeur: payload.vendeur || null,
            note: payload.note || null,
            emplacement_id: normalizedPayload.emplacement_id,
            ...(payload.repartition
              ? {
                  repartition: payload.repartition.map((item) => ({
                    emplacement_id: item.emplacement_id,
                    quantite: item.quantite,
                  })),
                }
              : {}),
          }
        : {};
    try {
      return unwrapOptionalMovement(
        await mutationRequest<unknown>(`/vins/${encodeURIComponent(wineId)}/mouvements/${action}`, {
          method: "POST",
          body: JSON.stringify(body),
        }),
      );
    } catch (error) {
      if (isNetworkFailure(error)) {
        throw new ApiError(
          "L’ajout de bouteilles nécessite une connexion.",
          0,
          "ACTION_INDISPONIBLE_HORS_LIGNE",
        );
      }
      throw error;
    }
  },

  async transferWine(
    wineId: string,
    payload: TransferWinePayload,
  ): Promise<TransferWineResult> {
    if (browserIsOffline()) {
      throw new ApiError(
        "Le déplacement de bouteilles nécessite une connexion.",
        0,
        "ACTION_INDISPONIBLE_HORS_LIGNE",
      );
    }
    await assertWineHasNoPendingMovements(wineId);
    try {
      const raw = await mutationRequest<unknown>(
        `/vins/${encodeURIComponent(wineId)}/mouvements/transfert`,
        {
          method: "POST",
          body: JSON.stringify({
            source_emplacement_id: payload.source_emplacement_id,
            destination_emplacement_id: payload.destination_emplacement_id,
            quantite: payload.quantite,
            date_effective: payload.date_effective ?? null,
            note: payload.note ?? null,
            ...(payload.coravin_ids ? { coravin_ids: payload.coravin_ids } : {}),
          }),
        },
      );
      const record = (raw ?? {}) as Record<string, unknown>;
      const values = Array.isArray(record.mouvements)
        ? record.mouvements
        : Array.isArray(record.movements)
          ? record.movements
          : [];
      return {
        mouvements: values.map((movement) =>
          normalizeMovement(movement as Record<string, unknown>),
        ),
        stock: Number(record.stock ?? 0),
        stocks_par_emplacement: Array.isArray(record.stocks_par_emplacement)
          ? normalizeWine({
              id: wineId,
              stocks_par_emplacement: record.stocks_par_emplacement,
            }).stocks_par_emplacement
          : undefined,
      };
    } catch (error) {
      if (isNetworkFailure(error)) {
        throw new ApiError(
          "Le déplacement de bouteilles nécessite une connexion.",
          0,
          "ACTION_INDISPONIBLE_HORS_LIGNE",
        );
      }
      throw error;
    }
  },

  async createCoravin(wineId: string, payload: CreateCoravinPayload): Promise<CoravinBottle> {
    const raw = await coravinMutation(wineId, `/vins/${encodeURIComponent(wineId)}/coravin`,
      { method: "POST" },
      {
        emplacement_id: payload.emplacement_id,
        date_effective: payload.date_effective,
        note: payload.note || null,
      },
    );
    const record = (raw ?? {}) as Record<string, unknown>;
    return normalizeCoravin((record.coravin ?? record) as Record<string, unknown>);
  },

  async finishCoravin(wineId: string, coravinId: string, payload: FinishCoravinPayload): Promise<Movement> {
    return unwrapMovement(await coravinMutation(
      wineId,
      `/vins/${encodeURIComponent(wineId)}/coravin/${encodeURIComponent(coravinId)}/terminer`,
      { method: "POST" },
      { date_effective: payload.date_effective, note: payload.note || null },
    ));
  },

  async deleteCoravin(wineId: string, coravinId: string, expectedModification?: string | null): Promise<void> {
    await coravinMutation(
      wineId,
      `/vins/${encodeURIComponent(wineId)}/coravin/${encodeURIComponent(coravinId)}`,
      { method: "DELETE", headers: ifMatchHeaders(expectedModification) },
    );
  },

  async deleteMovement(
    wineId: string,
    id: string,
    expectedModification?: string | null,
  ): Promise<void> {
    await mutationRequest<void>(
      `/vins/${encodeURIComponent(wineId)}/mouvements/${encodeURIComponent(id)}`,
      {
        method: "DELETE",
        headers: ifMatchHeaders(expectedModification),
      },
    );
  },

  async updateMovement(
    wineId: string,
    id: string,
    payload: UpdateMovementPayload,
    expectedModification?: string | null,
  ): Promise<Movement> {
    return unwrapMovement(
      await mutationRequest<unknown>(
        `/vins/${encodeURIComponent(wineId)}/mouvements/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          headers: ifMatchHeaders(expectedModification),
          body: JSON.stringify(payload),
        },
      ),
    );
  },

  async cancelMovement(
    wineId: string,
    id: string,
    payload: CancelMovementPayload,
    expectedModification?: string | null,
  ): Promise<Movement> {
    return unwrapMovement(
      await mutationRequest<unknown>(
        `/vins/${encodeURIComponent(wineId)}/mouvements/${encodeURIComponent(id)}/annulation`,
        {
          method: "POST",
          headers: ifMatchHeaders(expectedModification),
          body: JSON.stringify(payload),
        },
      ),
    );
  },

  async findDuplicates(payload: WineFields): Promise<Wine[]> {
    const raw = await request<unknown>("/vins/doublons", {
      method: "POST",
      body: JSON.stringify({
        ...wineBody(payload),
      }),
    });
    const record = (raw ?? {}) as Record<string, unknown>;
    const values = (record.doublons ?? []) as unknown[];
    return values.map((wine) => normalizeWine(wine as Record<string, unknown>));
  },

  async getSuggestions(
    field: "producteur" | "appellation" | "region",
    query = "",
  ): Promise<string[]> {
    const raw = await request<unknown>(
      `/vins/suggestions?champ=${field}&q=${encodeURIComponent(query)}`,
    );
    const record = (raw ?? {}) as Record<string, unknown>;
    const values = Array.isArray(raw)
      ? raw
      : ((record.suggestions ?? record.data ?? []) as unknown[]);
    return values.map(String);
  },

  async listLocations(): Promise<CellarLocation[]> {
    return requiredLocalValue(
      () => offlinePersistence.getCachedLocations(),
      "Les emplacements n’ont pas encore été enregistrés sur cet appareil.",
    );
  },

  async listPlaces(): Promise<CellarPlaceRecord[]> {
    return requiredLocalValue(
      () => offlinePersistence.getCachedPlaces(),
      "Les lieux n’ont pas encore été enregistrés sur cet appareil.",
    );
  },

  async createPlace(nom: string): Promise<CellarPlaceRecord> {
    return unwrapPlace(
      await mutationRequest<unknown>("/lieux", {
        method: "POST",
        body: JSON.stringify({ nom: nom.trim() }),
      }),
    );
  },

  async updatePlace(
    id: string,
    nom: string,
    expectedModification?: string | null,
  ): Promise<CellarPlaceRecord> {
    return unwrapPlace(
      await mutationRequest<unknown>(`/lieux/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: ifMatchHeaders(expectedModification),
        body: JSON.stringify({ nom: nom.trim() }),
      }),
    );
  },

  async deletePlace(
    id: string,
    destinationId: string | null,
    expectedModification?: string | null,
  ): Promise<void> {
    await mutationRequest<void>(`/lieux/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: ifMatchHeaders(expectedModification),
      body: JSON.stringify({ destination_id: destinationId }),
    });
  },

  async createLocation(payload: LocationFields): Promise<CellarLocation> {
    return unwrapLocation(
      await mutationRequest<unknown>("/emplacements", {
        method: "POST",
        body: JSON.stringify({
          lieu_id: payload.lieu_id,
          nom: payload.nom.trim(),
          capacite_totale: payload.capacite_totale,
        }),
      }),
    );
  },

  async updateLocation(
    id: string,
    payload: LocationFields,
    expectedModification?: string | null,
  ): Promise<CellarLocation> {
    return unwrapLocation(
      await mutationRequest<unknown>(`/emplacements/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: ifMatchHeaders(expectedModification),
        body: JSON.stringify({
          lieu_id: payload.lieu_id,
          nom: payload.nom.trim(),
          capacite_totale: payload.capacite_totale,
        }),
      }),
    );
  },

  async deleteLocation(
    id: string,
    destinationId: string | null,
    expectedModification?: string | null,
  ): Promise<void> {
    await mutationRequest<void>(`/emplacements/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: ifMatchHeaders(expectedModification),
      body: JSON.stringify({ destination_id: destinationId }),
    });
  },

  async downloadExport(kind: "vins" | "mouvements"): Promise<void> {
    const response = await fetchWithSessionRetry(`${API_ROOT}/export/${kind}.csv`, {
      cache: "no-store",
      headers: { Accept: "text/csv" },
    });
    if (!response.ok) {
      throw new ApiError("L’export n’a pas pu être préparé.", response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${kind}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  async downloadAllExports(): Promise<void> {
    const kinds = ["vins", "mouvements"] as const;
    const responses = await Promise.all(
      kinds.map((kind) =>
        fetchWithSessionRetry(`${API_ROOT}/export/${kind}.csv`, {
          cache: "no-store",
          headers: { Accept: "text/csv" },
        }),
      ),
    );
    const failed = responses.find((response) => !response.ok);
    if (failed) throw new ApiError("L’export n’a pas pu être préparé.", failed.status);
    const blobs = await Promise.all(responses.map((response) => response.blob()));
    blobs.forEach((blob, index) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${kinds[index]}.csv`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  },
};
